// A play as a value: start with newPlay(), then each move returns a new state or a reason code.
// The API replays every stored move through these same functions, so client and server agree.
import type { WordList } from '@wbwl/words'
import { ROWS, WORD_LENGTH } from './constants.ts'
import { burnCols, wordListFor, type Edition } from './edition.ts'
import { indexOf, maskOf, outOfVowels } from './letters.ts'
import { scoreWord, type Scoring, type WordScore } from './score.ts'

export type Phase = 'play' | 'phoenix' | 'done'
/** Why the rows stopped: all six played, every vowel burned, no legal word left, or the day ended first. */
export type RowsEnd = 'six_rows' | 'out_of_vowels' | 'no_moves' | 'day_over'
/**
 * How the phoenix round ended. 'no_word' means nothing in the ashes spelled a word, so it was
 * skipped for you; 'day_over' means the edition's day ended before the game did.
 */
export type PhoenixResult = 'raised' | 'skipped' | 'no_word' | 'day_over'

export interface PlayState {
  readonly rows: readonly string[]
  /** Burned letters, each once, in the order they burned. */
  readonly ashes: readonly string[]
  readonly phase: Phase
  readonly rowsEnd: RowsEnd | null
  readonly phoenix: string | null
  readonly phoenixResult: PhoenixResult | null
}

export type MoveRejection =
  | { readonly reason: 'wrong_phase'; readonly phase: Phase }
  | { readonly reason: 'wrong_length' }
  | { readonly reason: 'not_a_word' }
  | { readonly reason: 'already_played'; readonly row: number }
  | { readonly reason: 'burned_letter'; readonly letters: readonly string[] }

export type MoveResult =
  | { readonly ok: true; readonly state: PlayState; readonly score: WordScore; readonly burned: readonly string[] }
  | ({ readonly ok: false } & MoveRejection)

export type PhoenixRejection =
  | { readonly reason: 'wrong_phase'; readonly phase: Phase }
  | { readonly reason: 'wrong_length' }
  | { readonly reason: 'not_a_word' }
  | { readonly reason: 'not_in_ashes'; readonly letters: readonly string[] }
  | { readonly reason: 'repeated_letter'; readonly letters: readonly string[] }

export type PhoenixMoveResult =
  | { readonly ok: true; readonly state: PlayState; readonly score: WordScore }
  | ({ readonly ok: false } & PhoenixRejection)

export interface PhoenixOption {
  readonly word: string
  readonly score: WordScore
}

export interface PlayScore {
  readonly rows: readonly WordScore[]
  readonly rowsTotal: number
  /** The raised phoenix's score; null if it was skipped or not played yet. */
  readonly phoenix: WordScore | null
  readonly total: number
}

export const newPlay = (): PlayState => ({ rows: [], ashes: [], phase: 'play', rowsEnd: null, phoenix: null, phoenixResult: null })

const normalize = (word: string) => word.trim().toUpperCase()
const unique = (letters: Iterable<string>) => [...new Set(letters)]

/**
 * Plays a word on the next row. Checks run in this order: wrong_length, not_a_word,
 * already_played, burned_letter. (A played word always holds a burned letter, so already_played
 * has to come first to ever be reported.) When the rows end, the state moves to the phoenix round,
 * or straight to done if nothing in the ashes spells a word.
 */
export function applyMove(edition: Edition, state: PlayState, word: string, words: WordList = wordListFor(edition)): MoveResult {
  if (state.phase !== 'play') return { ok: false, reason: 'wrong_phase', phase: state.phase }
  const w = normalize(word)
  if (w.length !== WORD_LENGTH) return { ok: false, reason: 'wrong_length' }
  if (!words.has(w)) return { ok: false, reason: 'not_a_word' }
  const playedAt = state.rows.indexOf(w)
  if (playedAt >= 0) return { ok: false, reason: 'already_played', row: playedAt }
  const blocked = unique([...w].filter((l) => state.ashes.includes(l)))
  if (blocked.length) return { ok: false, reason: 'burned_letter', letters: blocked }

  const ashes = [...state.ashes]
  const burned: string[] = []
  for (const c of burnCols(edition, state.rows.length)) {
    const l = w[c]
    if (!ashes.includes(l)) { ashes.push(l); burned.push(l) }
  }
  const rows = [...state.rows, w]
  let next: PlayState = { ...state, rows, ashes }
  const end = rows.length >= ROWS ? 'six_rows' : outOfVowels(maskOf(ashes)) ? 'out_of_vowels' : hasLegalWord(next, words) ? null : 'no_moves'
  if (end) {
    next = hasPhoenixWord(ashes, words)
      ? { ...next, phase: 'phoenix', rowsEnd: end }
      : { ...next, phase: 'done', rowsEnd: end, phoenixResult: 'no_word' }
  }
  return { ok: true, state: next, score: scoreWord(w, edition), burned }
}

/**
 * Raises the phoenix: any word from the ashes, each ash at most once. Checks run in this order:
 * wrong_length, not_a_word, not_in_ashes, repeated_letter.
 */
export function applyPhoenix(edition: Edition, state: PlayState, word: string, words: WordList = wordListFor(edition)): PhoenixMoveResult {
  if (state.phase !== 'phoenix') return { ok: false, reason: 'wrong_phase', phase: state.phase }
  const w = normalize(word)
  if (w.length !== WORD_LENGTH) return { ok: false, reason: 'wrong_length' }
  if (!words.has(w)) return { ok: false, reason: 'not_a_word' }
  const missing = unique([...w].filter((l) => !state.ashes.includes(l)))
  if (missing.length) return { ok: false, reason: 'not_in_ashes', letters: missing }
  const repeated = unique([...w].filter((l, i) => w.indexOf(l) !== i))
  if (repeated.length) return { ok: false, reason: 'repeated_letter', letters: repeated }
  return { ok: true, state: { ...state, phase: 'done', phoenix: w, phoenixResult: 'raised' }, score: scoreWord(w, edition) }
}

export function skipPhoenix(state: PlayState): { readonly ok: true; readonly state: PlayState } | { readonly ok: false; readonly reason: 'wrong_phase'; readonly phase: Phase } {
  if (state.phase !== 'phoenix') return { ok: false, reason: 'wrong_phase', phase: state.phase }
  return { ok: true, state: { ...state, phase: 'done', phoenixResult: 'skipped' } }
}

/**
 * Ends a game its day ran out on (midnight Pacific), keeping the score so far: mid-rows it ends
 * the rows as day_over, and either way the phoenix is day_over (never raised).
 */
export function endPlay(state: PlayState): { readonly ok: true; readonly state: PlayState } | { readonly ok: false; readonly reason: 'wrong_phase'; readonly phase: Phase } {
  if (state.phase === 'done') return { ok: false, reason: 'wrong_phase', phase: state.phase }
  const rowsEnd = state.phase === 'play' ? 'day_over' : state.rowsEnd
  return { ok: true, state: { ...state, phase: 'done', rowsEnd, phoenixResult: 'day_over' } }
}

/** Every word the ashes can spell (five different letters, all burned), best first. */
export function phoenixOptions(edition: Edition, ashes: readonly string[], words: WordList = wordListFor(edition)): PhoenixOption[] {
  const have = maskOf(ashes)
  const index = indexOf(words)
  return index.words
    .filter((_, i) => index.distinct[i] && !(index.masks[i] & ~have))
    .map((word) => ({ word, score: scoreWord(word, edition) }))
    .sort((a, b) => b.score.total - a.score.total || (a.word < b.word ? -1 : 1))
}

function hasPhoenixWord(ashes: readonly string[], words: WordList): boolean {
  const have = maskOf(ashes)
  const index = indexOf(words)
  for (let i = 0; i < index.words.length; i++) if (index.distinct[i] && !(index.masks[i] & ~have)) return true
  return false
}

/** Words that can go on the next row right now, in word-list order. */
export function legalWords(state: PlayState, words: WordList): string[] {
  if (state.phase !== 'play') return []
  const burned = maskOf(state.ashes)
  const index = indexOf(words)
  return index.words.filter((w, i) => !(index.masks[i] & burned) && !state.rows.includes(w))
}

function hasLegalWord(state: PlayState, words: WordList): boolean {
  const burned = maskOf(state.ashes)
  const index = indexOf(words)
  for (let i = 0; i < index.words.length; i++) {
    if (!(index.masks[i] & burned) && !state.rows.includes(index.words[i])) return true
  }
  return false
}

export function scorePlay(board: Scoring, state: Pick<PlayState, 'rows' | 'phoenix'>): PlayScore {
  const rows = state.rows.map((w) => scoreWord(w, board))
  const rowsTotal = rows.reduce((s, r) => s + r.total, 0)
  const phoenix = state.phoenix ? scoreWord(state.phoenix, board) : null
  return { rows, rowsTotal, phoenix, total: rowsTotal + (phoenix?.total ?? 0) }
}

export type ReplayResult =
  | { readonly ok: true; readonly state: PlayState }
  | { readonly ok: false; readonly at: number | 'phoenix'; readonly rejection: MoveRejection | PhoenixRejection }

/**
 * Rebuilds a play from stored moves. phoenix: a word raises it, null skips it, undefined leaves
 * the play where the rows left it. Skipping is a no-op when the play already ended with no phoenix.
 */
export function replay(edition: Edition, rows: readonly string[], phoenix?: string | null, words: WordList = wordListFor(edition)): ReplayResult {
  let state = newPlay()
  for (const [i, word] of rows.entries()) {
    const r = applyMove(edition, state, word, words)
    if (!r.ok) return { ok: false, at: i, rejection: r }
    state = r.state
  }
  if (phoenix === undefined || (phoenix === null && state.phoenixResult === 'no_word')) return { ok: true, state }
  const r = phoenix === null ? skipPhoenix(state) : applyPhoenix(edition, state, phoenix, words)
  return r.ok ? { ok: true, state: r.state } : { ok: false, at: 'phoenix', rejection: r }
}
